import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const NotFound = () => {
  /** ====== Variants định nghĩa chuyển động tái sử dụng ====== */
  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const staggerWrap = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.08,
        when: "beforeChildren",
      },
    },
  };

  /** 404 bounce + letterSpacing nhẹ để tạo cảm giác “đàn hồi” */
  const fourOhFour = {
    hidden: { opacity: 0, scale: 0.9, letterSpacing: "-0.04em" },
    show: {
      opacity: 1,
      scale: 1,
      letterSpacing: "0em",
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 14,
      },
    },
  };

  /** Icon kính lúp vẽ nét (path draw) */
  const pathDraw = {
    hidden: { pathLength: 0, opacity: 0 },
    show: {
      pathLength: 1,
      opacity: 1,
      transition: { duration: 1.2, ease: "easeInOut" },
    },
  };

  return (
    <section className="relative flex h-screen items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-sky-100 p-6">
      {/* Card chính */}
      <motion.div
        className="relative h-full w-full flex items-center justify-center flex-col rounded-2xl border border-white/50 bg-white/70 p-8 shadow-xl backdrop-blur-md md:p-10"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        role="group"
        aria-label="Page not found"
      >
        {/* SVG minh hoạ vẽ nét */}
        <div className="mx-auto mb-6 flex items-center justify-center">
          <motion.svg
            width="72"
            height="72"
            viewBox="0 0 24 24"
            fill="none"
            className="drop-shadow-sm"
            initial="hidden"
            animate="show"
            aria-hidden
          >
            <motion.circle
              cx="11"
              cy="11"
              r="7"
              stroke="currentColor"
              className="text-blue-600"
              strokeWidth="1.6"
              variants={pathDraw}
            />
            <motion.line
              x1="16.65"
              y1="16.65"
              x2="21"
              y2="21"
              stroke="currentColor"
              className="text-blue-600"
              strokeWidth="1.6"
              strokeLinecap="round"
              variants={pathDraw}
              transition={{ delay: 0.3, duration: 0.9 }}
            />
          </motion.svg>
        </div>

        {/* Nội dung + stagger */}
        <motion.div
          variants={staggerWrap}
          initial="hidden"
          animate="show"
          className="text-center"
        >
          <motion.h1
            variants={fourOhFour}
            className="mb-3 text-7xl font-extrabold tracking-tight text-blue-600 md:text-8xl"
          >
            404
          </motion.h1>

          <motion.h2
            variants={fadeUp}
            className="mb-3 text-2xl font-semibold text-gray-800 md:text-4xl"
          >
            Oops! Page Not Found
          </motion.h2>

          <motion.p
            variants={fadeUp}
            className="mx-auto mb-8 max-w-md text-lg text-gray-600"
          >
            The page you’re looking for doesn’t exist or has been moved.
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="flex flex-col items-center justify-center gap-3 sm:flex-row"
          >
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-5 py-3 text-base font-semibold text-white shadow-sm transition
                         hover:shadow-md hover:brightness-110 active:scale-[0.99]
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/70 focus-visible:ring-offset-2"
            >
              ← Back to Homepage
            </Link>

            <Link
              to={-1}
              className="inline-flex items-center justify-center rounded-xl border border-blue-200 bg-white/60 px-5 py-3 text-base font-medium text-blue-700 shadow-sm transition
                         hover:bg-white hover:shadow-md active:scale-[0.99]
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 focus-visible:ring-offset-2"
            >
              Go Back
            </Link>
          </motion.div>
        </motion.div>

        {/* Shine effect rất nhẹ khi hover card */}
        <div className="pointer-events-none absolute inset-0 rounded-2xl">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-white/0 via-white/0 to-white/30 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        </div>
      </motion.div>
    </section>
  );
};

export default NotFound;
